import { plural } from '@cian/utils';

import { get, has } from 'lodash';

import upsellingAbTest from '../../scripts/services/upselling-ab-test';
import app from '../../scripts/app';
import { currencyByKey as currencyEnum } from '../../scripts/enums/currency';
import coworkingPriceList from '../../scripts/enums/coworking-price-list';

import template from './offer-preview.jade';

const priceTypeText = {
  squareMeter: 'м&sup2;',
  sotka: 'сотку',
  hectare: 'гектар'
};

app.directive('cianAfOfferPreview', [function () {
  return {
    template,
    scope: {},
    bindToController: {
      offerId: '=',
      editAction: '&',
    },
    controller: [
      '$scope',
      '$sce',
      '$filter',
      '$log',
      'addFormService',
      'latestBackendResponseService',
      'categoryService',
      'userService',
      'popupService',
      'deviceService',
      'coworkingService',
      function (
        $scope,
        $sce,
        $filter,
        $log,
        addFormService,
        latestBackendResponseService,
        categoryService,
        userService,
        popupService,
        deviceService,
        coworkingService,
      ) {
        this.isMobile = deviceService.isMobile;
        this.editLoading = false;
        this.edit = () => {
          this.editLoading = true;
          this.editAction();
        };

        this.vcBannerVisible = false;
        this.vcBannerPluralizeDays = {
          one: 'В ДЕНЬ',
          few: 'НА {} ДНЯ',
          many: 'НА {} ДНЕЙ'
        };

        this.offerPreviewData = {
          address: get(addFormService, 'ad.geo.userInput'),
          title: getOfferPreviewTitle()
        };

        function getOfferPreviewTitle() {
          const ad = addFormService?.ad;
          const isCoworking = coworkingService.isCoworkingCategory();

          const { dealType, rentDuration } = categoryService.getInfoByCategory(get(addFormService, 'ad.category'));
          const titleParts = [addFormService.getOfferShortTitle()];

          const totalArea = get(addFormService, 'ad.totalArea');
          if (totalArea) {
            titleParts.push(`${totalArea} м<sup>2</sup>`);
          }

          const workplaceCount = ad?.workplaceCount;
          const isFlexOffice = coworkingService.isFlexOffice();

          if (isCoworking && workplaceCount > 0) {
            const workplaceCaption = isFlexOffice
              ? `на ${workplaceCount} чел.`
              : `${workplaceCount} ${plural(workplaceCount, ['место', 'места', 'мест'])}`

            titleParts.push(workplaceCaption);
          }

          const floorNumber = get(addFormService, 'ad.floorNumber');
          const floorsCount = get(addFormService, 'ad.building.floorsCount');
          if (floorNumber && floorsCount) {
            titleParts.push(`этаж ${floorNumber} / ${floorsCount}`);
          }

          let price = get(addFormService, 'ad.bargainTerms.price');

          if (isCoworking && !isFlexOffice) {
            price = addFormService.getFieldValue('bargainTerms.priceForWorkplace');
          }

          const priceCurrency = get(addFormService, 'ad.bargainTerms.currency');
          if (price) {
            price = $filter('price')(price);
            price += ' ' + currencyEnum[priceCurrency];
            if (dealType === 'rent') {
              price += '/';

              const paymentPeriod = get(addFormService, 'ad.bargainTerms.paymentPeriod');
              if (paymentPeriod === 'annual') {
                price += 'год';
              } else if (rentDuration === 'short') {
                price += 'сут.'
              } else {
                price += 'мес.'
              }

              const { priceType } = ad?.bargainTerms;

              if (isCoworking) {
                const coworkingPrice = coworkingPriceList.find(
                  coworkingElement => coworkingElement.coworkingOfferTypes.includes(ad.coworkingOfferType)
                );

                if (coworkingPrice) {
                  price += ` ${coworkingPrice.text.toLowerCase()}`;
                }
              } else {
                if (priceType in priceTypeText) {
                  price += ` за ${priceTypeText[priceType]}`;
                }
              }
            }

            titleParts.push(price);
          }

          return $sce.trustAsHtml(titleParts.join(', '));
        }

        const offerTerms = get(addFormService, 'ad.publishTerms.terms', []); // условия размещения с которыми куплено объявление
        const availableTerms = get(latestBackendResponseService.data, 'publishTerms.terms', []); // условия размещения доступные пользователю

        /**
         * true - если куплен Премиум или Платное, пропускаем пакеты(бандлы, например Премиум+ВЦ одной услугой)
         */
        const offerHasPaidOrPremium = offerTerms.some(function (offerTermsItem) {
          const services = get(offerTermsItem, 'services', []);
          const periodType = get(offerTermsItem, 'type');
          const tariffGridType = get(offerTermsItem, 'tariffIdentificator.tariffGridType');

          const firstService = services[0];

          // Если основная услуга премиум посуточный - не показываем банер
          // Подробности в CSSSR-8795
          const offerItemIsNotPremiumDailyTermless = firstService !== 'premium' || periodType !== 'dailyTermless' || tariffGridType !== 'PackageTariff';

          return services.length === 1 && (firstService === 'paid' || firstService === 'premium') && offerItemIsNotPremiumDailyTermless;
        });

        /**
         * true - если НЕ куплено Выделение Цветом
         */
        const offerHasNotHighlight = offerTerms.every(function (offerTermsItem) {
          const services = get(offerTermsItem, 'services', []);
          return services.some(item => item !== 'highlight');
        });

        /**
         * true - если бек вернул условия размещения, где есть услуга Выделение цветом
         */
        const availableTermsHasHighlight = availableTerms.some(function (availableTermsItem) {
          const services = get(availableTermsItem, 'services', []);
          const firstService = services[0];
          const hasPeriod = has(availableTermsItem, 'publishOptions.periods[0]');

          return services.length === 1 && firstService === 'highlight' && hasPeriod;
        });

        const highlightOption = availableTerms.find(function (availableTermsItem) {
          const services = get(availableTermsItem, 'services', []);
          const firstService = services[0];

          return (services.length === 1 && firstService === 'highlight')
        });

        if (upsellingAbTest.isHighlightAvailableForUser(userService.user.cianUserId) && offerHasPaidOrPremium && offerHasNotHighlight && availableTermsHasHighlight) {
          if (highlightOption) {
            const firstPeriod = get(highlightOption, 'publishOptions.periods[0]'); // Считаем что у первого периода будет минимальная цена
            this.vcBannerVisible = true;
            this.vcBannerPrice = firstPeriod.price;
            this.vcBannerDays = firstPeriod.days;
          }
        }

        this.openModal = () => {
          const options = {
            highlightOption,
            offerTerms,
            offerId: this.offerId
          };
          const modalInstance = popupService.openPopup('upsaleHighlight', { options });
          modalInstance.result
            .then((result) => {
              if (result === 'success') {
                this.vcBannerVisible = false;
                popupService.openPopup('upsaleHighlightSuccess');
              }
            })
            .catch(err => {
              // err.$error
              // Так закидываем ошибку из попапа, игнорируя ошибки типа закрытие
              if (err.$error) {
                popupService.openPopup('upsaleHighlightFailure');
                throw err.$error;
              }
            });
        };
      }],
    controllerAs: 'offerPreviewCtrl'
  }
}]);
